import { vaultFilesApi } from "@api";
import { ErrorAlert } from "@components/ErrorAlert";
import { queriesVaultFiles } from "@query";
import {
  WSAlert,
  WSControlGroup,
  WSDropFileInput,
  WSElement,
  WSFlexBox,
  WSForm,
  WSIcon,
  WSList,
  WSText,
  WSTextInput
} from "@wingspanhq/fe-component-library";
import {
  DocumentTemplateSignerRole,
  VaultFileKind,
  VaultFileVisibility
} from "@wingspanhq/wingspan-api-sdk";
import { useWSMutation } from "@ws-react-query";
import React from "react";
import { useFormContext } from "react-hook-form";

import { openInNewTab } from "../../../../../shared/utils/openInNewTab";
import { useESignatureAgreementTemplatesInfo } from "../../../../../userFeatures/useESignatureAgreementTemplatesInfo";
import styles from "../../../components/CreateRequirementDefinitionForm/styles.module.scss";
import { RequirementDefinitionSignatureFormValues } from "../../../types";

import { useMutationCreateSignatureRequirementDefinitionDataSource } from "./useMutationCreateSignatureRequirementDefinitionDataSource";

/**
 * The v3 replacement for the v1 member-files multipart upload: presign
 * (`createVaultFileUpload`) → PUT the bytes at the returned storage URL →
 * register the vault file. A document template's `fileId` must reference a
 * private `Document`-kind vault file, so the e-signature source document moves
 * to the vault along with the template create it feeds.
 *
 * Composed by hand rather than added to the registry: the registry is 1:1 with
 * the SDK, and this orchestrates two SDK calls around a byte PUT that is not an
 * SDK call at all.
 *
 * TODO(v3-blocker): the byte PUT fails its CORS preflight in a browser — the
 * vault storage bucket does not yet allow cross-origin PUT from app origins
 * (same blocker as the shared-file upload; `vaultFiles.itest.ts` covers the
 * flow but runs under Node, which does not enforce CORS).
 */
const useMutationUploadVaultFile = () =>
  useWSMutation<string, unknown, File>(async file => {
    const upload = await vaultFilesApi.createVaultFileUpload({
      vaultFileUploadCreate: {
        filename: file.name,
        mimeType: file.type,
        contentLength: file.size
      }
    });

    const uploadResponse = await fetch(upload.uploadUrl, {
      method: "PUT",
      headers: upload.requiredHeaders,
      body: file
    });

    if (!uploadResponse.ok) {
      throw new Error(
        `Failed to upload file (${uploadResponse.status} ${uploadResponse.statusText})`
      );
    }

    const vaultFile = await vaultFilesApi.createVaultFile({
      vaultFileCreate: {
        uploadId: upload.uploadId,
        title: file.name,
        filename: file.name,
        mimeType: file.type,
        kind: VaultFileKind.DOCUMENT,
        visibility: VaultFileVisibility.PRIVATE
      }
    });

    if (!vaultFile.id) {
      throw new Error("Uploaded file was registered without an id");
    }

    return vaultFile.id;
  });

const FilePanel: React.FC<{ fileId: string; onRemove(): void }> = ({
  fileId,
  onRemove
}) => {
  const queryVaultFile = queriesVaultFiles.getVaultFile.useQuery({
    fileId
  });

  const file = queryVaultFile.data;

  return queryVaultFile.isError && queryVaultFile.error ? (
    <ErrorAlert context="BrowsingFiles" error={queryVaultFile.error} />
  ) : (
    <WSElement
      className={styles.selectedFile}
      p="XL"
      shimmer={queryVaultFile.isLoading}
    >
      <WSFlexBox.CenterY wrap="nowrap">
        <WSText className={styles.selectedFileName} singleLine>
          {file?.filename}
        </WSText>
        <WSFlexBox>
          <WSIcon
            block
            color="gray500"
            mx="M"
            name="exit"
            size="M"
            onClick={() => {
              onRemove();
            }}
          />
        </WSFlexBox>
      </WSFlexBox.CenterY>
    </WSElement>
  );
};

export const CreateSignaturePartialForm: React.FC = () => {
  const { setValue } = useFormContext();

  const customInfo = useESignatureAgreementTemplatesInfo();
  const [createDocumentTemplate, createDocumentTemplateMeta] =
    useMutationCreateSignatureRequirementDefinitionDataSource();

  const [uploadFile, uploadFileMeta] = useMutationUploadVaultFile();

  return (
    <WSList gap="XL">
      <WSAlert
        action={
          customInfo?.actionTitle
            ? {
                onClick: () => openInNewTab(customInfo.actionLink),
                text: customInfo.actionTitle
              }
            : undefined
        }
        theme="info"
        title="About e-Signature requirements"
      >
        Contractor(s) assigned with this requirement will be asked to e-sign the
        document uploaded here.
        {customInfo?.description ? (
          <>
            <WSElement mt="M" />
            {customInfo.description}
          </>
        ) : null}
      </WSAlert>

      <WSForm.Field
        component={WSTextInput}
        componentProps={{
          hidden: true
        }}
        invisible={true}
        label="File"
        name="fileId"
      />

      <WSForm.Field
        component={WSTextInput}
        componentProps={{
          hidden: true
        }}
        invisible={true}
        label="Template ID"
        name="signedDocumentTemplateId"
      />

      <WSForm.Values names={["fileId", "requiredSignatures"]}>
        {({
          fileId,
          requiredSignatures
        }: {
          fileId?: RequirementDefinitionSignatureFormValues["fileId"];
          requiredSignatures?: RequirementDefinitionSignatureFormValues["requiredSignatures"];
        }) => (
          <>
            <WSForm.Field
              component={WSControlGroup}
              componentProps={{
                disabled: !!fileId,
                options: [
                  {
                    label: "Contractor signature required only",
                    value: "collaborator"
                  },
                  {
                    label:
                      "Both contractor and company signatures are required",
                    value: "mutual"
                  }
                ],
                required: true,
                type: "radio"
              }}
              hidden={!!fileId}
              label="Required signatures"
              name="requiredSignatures"
            />

            <WSElement
              invisible={!!fileId}
              shimmer={
                uploadFileMeta.isLoading || createDocumentTemplateMeta.isLoading
              }
            >
              <WSText.ParagraphSm color="gray500" mb="S">
                Upload e-Signature file *
              </WSText.ParagraphSm>
              <WSDropFileInput
                accept=".pdf"
                buttonProps={{
                  type: "button"
                }}
                buttonText="Browse files"
                name="dropFileInput"
                onDropAccepted={async accepted => {
                  const file = accepted[0];

                  await uploadFile(file, {
                    onSuccess: uploadedFileId => {
                      createDocumentTemplate(
                        {
                          documentTemplateCreate: {
                            fileId: uploadedFileId,
                            signerRoles:
                              requiredSignatures === "collaborator"
                                ? [DocumentTemplateSignerRole.PAYEE]
                                : [
                                    DocumentTemplateSignerRole.PAYEE,
                                    DocumentTemplateSignerRole.PAYER
                                  ],
                            title: file.name
                          }
                        },
                        {
                          onError: () => {
                            setValue("fileId", "");
                            setValue("signedDocumentTemplateId", "");
                          },
                          onSuccess: template => {
                            setValue("signedDocumentTemplateId", template.id);
                            setValue("fileId", uploadedFileId);
                          }
                        }
                      );
                    }
                  });
                }}
              />
            </WSElement>

            {fileId ? (
              <WSList gap="S">
                <FilePanel
                  fileId={fileId}
                  onRemove={() => {
                    setValue("fileId", "");
                  }}
                />

                <WSText.ParagraphSm color="gray500">
                  Required signatures:{" "}
                  {requiredSignatures === "collaborator"
                    ? "Contractor"
                    : "Both contractor and company"}
                </WSText.ParagraphSm>
              </WSList>
            ) : null}
          </>
        )}
      </WSForm.Values>
      <WSText.ParagraphXs color="gray400" weight="book">
        Wingspan can auto-fill known info like names, emails, custom fields
        which you can configure in the next step.
      </WSText.ParagraphXs>

      <ErrorAlert
        context="UploadFile"
        error={uploadFileMeta.error || createDocumentTemplateMeta.error}
      />
    </WSList>
  );
};
